import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { AppPath } from 'appConstants';
import { MfaChallenge } from 'components/mfaChallenge/MfaChallenge';
import Page from 'components/shared/Page';
import successIcon from 'images/Icon_Mfa_Success.png';
import {
  consumeMfaReturnUrl,
  noteReturnedToSite,
  peekMfaReturnUrl,
  wasJustReturnedToSite,
} from 'helpers/mfa/mfaReturnUrl';
import { MFA_SET_UP_MESSAGE, MfaGrantResult, describeMfaGrant } from './models';

type Stage =
  | { name: 'refused'; site: string }
  | { name: 'challenge' }
  | { name: 'verified'; result: MfaGrantResult | null; setUp: boolean };

const stageOnArrival = (): Stage => {
  const returnUrl = peekMfaReturnUrl();
  return returnUrl && wasJustReturnedToSite(returnUrl)
    ? { name: 'refused', site: new URL(returnUrl).hostname }
    : { name: 'challenge' };
};

export const MfaChallengePage: React.FC<RouteComponentProps> = () => {
  const [stage, setStage] = useState<Stage>(stageOnArrival);

  const goBack = () => navigate(AppPath.DASHBOARD);

  const returned = useRef(false);
  const returnNow = () => {
    if (returned.current) return;
    returned.current = true;
    const returnUrl = consumeMfaReturnUrl();
    if (returnUrl) {
      noteReturnedToSite(returnUrl);
      window.location.assign(returnUrl);
    } else {
      navigate(AppPath.DASHBOARD);
    }
  };

  // A refused return url is cleared so a later visit to this page in the same tab can't reuse it.
  useEffect(() => {
    if (stage.name === 'refused') consumeMfaReturnUrl();
  }, [stage]);

  return (
    <Page bypassPageAccess className="MfaChallengePage">
      <div className="WidthContent">
        <div className="MfaChallengeCard">
          {stage.name === 'refused' && (
            <div className="MfaChallenge MfaPageLayout">
              <h1>We couldn't take you back</h1>
              <p className="MfaIntro">
                Your verification was not accepted by {stage.site}. This usually
                means you are signed in there with a different account from the
                one you are using here.
              </p>
              <p className="MfaIntro">
                Sign out of that site, sign in again with this account and try
                once more.
              </p>
              <button
                type="button"
                className="Btn BtnOrange MfaPrimary"
                onClick={goBack}
              >
                Back to dashboard
              </button>
            </div>
          )}
          {stage.name === 'challenge' && (
            <MfaChallenge
              mode="verify"
              layout="page"
              onComplete={(result, setUp) =>
                setStage({ name: 'verified', result, setUp })
              }
              onCancel={goBack}
            />
          )}
          {stage.name === 'verified' && (
            <div className="MfaSuccess">
              <img src={successIcon} alt="" className="MfaSuccessIcon" />
              <h2>
                {stage.setUp
                  ? 'Congratulations!'
                  : "You're all set to access pupil information."}
              </h2>
              {stage.setUp ? (
                <p className="MfaSuccessLead">{MFA_SET_UP_MESSAGE}</p>
              ) : (
                stage.result && (
                  <p className="MfaSuccessLead">
                    {describeMfaGrant(stage.result)}
                  </p>
                )
              )}
              {stage.setUp && (
                <p className="MfaIntro">Click here to access your content</p>
              )}
              <button
                type="button"
                className="Btn BtnOrange MfaPrimary"
                onClick={returnNow}
              >
                Let’s go!
              </button>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

export default MfaChallengePage;
